import React, { useState } from 'react'
import { useKPIRevenueDetail, useFiscalWeeks } from '../lib/data-service'
import { useFiscalWeekContext } from '../contexts/FiscalWeekContext'
import GlobalHeader from '../components/GlobalHeader'
import { BackButton } from '../components/BackButton'
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown, 
  Search,
  RefreshCw,
  Package,
  Calculator
} from 'lucide-react'

export default function GrossMarginDetail() {
  const { selectedFiscalWeek } = useFiscalWeekContext()
  const [searchTerm, setSearchTerm] = useState('')
  const [sortField, setSortField] = useState<'name' | 'totalRevenue' | 'grossMargin' | 'marginPercent'>('grossMargin')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')

  // Fetch detailed data (we'll use revenue detail as base and enhance with margin calculations)
  const { data: products, isLoading, error, refetch } = useKPIRevenueDetail(selectedFiscalWeek, 50)
  const { data: fiscalWeeks } = useFiscalWeeks()

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  const formatPercent = (value: number) => {
    return `${value.toFixed(1)}%`
  }

  const handleSort = (field: 'name' | 'totalRevenue' | 'grossMargin' | 'marginPercent') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  // Enhanced products with estimated gross margin (using average cost estimation)
  const enhancedProducts = products?.map(product => {
    // Estimate cost using industry average for demonstration
    // In real implementation, this would use actual cost data from the GM API
    const estimatedCostRatio = 0.55 // Assume 55% cost ratio for estimation
    const estimatedCost = product.totalRevenue * estimatedCostRatio
    const grossMargin = product.totalRevenue - estimatedCost
    const marginPercent = product.totalRevenue > 0 ? (grossMargin / product.totalRevenue) * 100 : 0

    return {
      ...product,
      estimatedCost,
      grossMargin,
      marginPercent
    }
  }) || []

  // Filter and sort products
  const filteredAndSortedProducts = enhancedProducts
    .filter(product => 
      product.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      let aValue: number | string
      let bValue: number | string

      switch (sortField) {
        case 'name':
          aValue = a.name
          bValue = b.name
          break
        case 'totalRevenue':
          aValue = a.totalRevenue
          bValue = b.totalRevenue
          break
        case 'grossMargin':
          aValue = a.grossMargin
          bValue = b.grossMargin
          break
        case 'marginPercent':
          aValue = a.marginPercent
          bValue = b.marginPercent
          break
        default:
          aValue = a.grossMargin
          bValue = b.grossMargin
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue)
      }

      const numA = typeof aValue === 'number' ? aValue : 0
      const numB = typeof bValue === 'number' ? bValue : 0
      
      return sortDirection === 'asc' ? numA - numB : numB - numA
    })

  if (isLoading) {
    return (
      <div className="space-y-6">
        <GlobalHeader 
          title="Gross Margin Deep Dive"
          subtitle="Loading detailed gross margin analysis..."
          isLoading={true}
        />
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-muted rounded-lg"></div>
          <div className="h-64 bg-muted rounded-lg"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <GlobalHeader 
          title="Gross Margin Deep Dive"
          subtitle="Error loading gross margin data"
        />
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6">
          <p className="text-destructive">Failed to load gross margin data. Please try again.</p>
          <button 
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/90 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  // Check if we have any data
  if (!products || products.length === 0) {
    return (
      <div className="space-y-6">
        <GlobalHeader 
          title="Gross Margin Deep Dive"
          subtitle="No detailed SKU data available"
        />
        
        <div className="bg-muted/50 border border-muted rounded-lg p-8 text-center">
          <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No SKU Data Available</h3>
          <p className="text-muted-foreground mb-4">
            Detailed SKU-level gross margin data is not available in the current dataset. 
            The original CSV files contain this data, but only summary data was synced to the database.
          </p>
          <BackButton href="/dashboard" label="Back to Dashboard" />
        </div>
      </div>
    )
  }

  // Calculate summary metrics
  const totalRevenue = enhancedProducts.reduce((sum, p) => sum + p.totalRevenue, 0)
  const totalGrossMargin = enhancedProducts.reduce((sum, p) => sum + p.grossMargin, 0)
  const overallMarginPercent = totalRevenue > 0 ? (totalGrossMargin / totalRevenue) * 100 : 0

  return (
    <div className="space-y-6">
      {/* Global Header */}
      <GlobalHeader 
        title="Gross Margin Deep Dive"
        subtitle="Detailed gross margin analysis by SKU with cost breakdown"
        onRefresh={() => refetch()}
        isLoading={isLoading}
      />

      {/* Back Button */}
      <BackButton href="/dashboard" label="Back to Dashboard" className="mb-4" />

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <DollarSign className="w-5 h-5 text-green-500" />
            <div>
              <p className="text-sm text-muted-foreground">Total Revenue</p>
              <p className="text-xl font-bold text-foreground">{formatCurrency(totalRevenue)}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Calculator className="w-5 h-5 text-blue-500" />
            <div>
              <p className="text-sm text-muted-foreground">Gross Margin $</p>
              <p className="text-xl font-bold text-foreground">{formatCurrency(totalGrossMargin)}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <TrendingUp className="w-5 h-5 text-purple-500" />
            <div>
              <p className="text-sm text-muted-foreground">Margin %</p>
              <p className="text-xl font-bold text-foreground">{formatPercent(overallMarginPercent)}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Package className="w-5 h-5 text-orange-500" />
            <div>
              <p className="text-sm text-muted-foreground">SKUs Analyzed</p>
              <p className="text-xl font-bold text-foreground">{enhancedProducts.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search Filter */}
      <div className="flex items-center space-x-2 max-w-md">
        <Search className="w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search products..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        />
      </div>

      {/* Products Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th
                  className="text-left p-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Product</span>
                    {sortField === 'name' && (
                      <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th
                  className="text-left p-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                  onClick={() => handleSort('totalRevenue')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Revenue</span>
                    {sortField === 'totalRevenue' && (
                      <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th
                  className="text-left p-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                  onClick={() => handleSort('grossMargin')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Gross Margin $</span>
                    {sortField === 'grossMargin' && (
                      <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th
                  className="text-left p-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                  onClick={() => handleSort('marginPercent')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Margin %</span>
                    {sortField === 'marginPercent' && (
                      <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th className="text-left p-4 font-medium text-muted-foreground">
                  Channel Split
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedProducts.map((product) => (
                <tr key={product.name} className="border-b border-border hover:bg-muted/30 transition-colors">
                  <td className="p-4">
                    <div className="font-medium text-foreground">
                      {product.name}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="font-medium text-foreground">
                      {formatCurrency(product.totalRevenue)}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="font-medium text-foreground">
                      {formatCurrency(product.grossMargin)}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className={`font-medium ${
                      product.marginPercent >= 50 ? 'text-green-600' :
                      product.marginPercent >= 30 ? 'text-yellow-600' :
                      'text-red-600'
                    }`}>
                      {formatPercent(product.marginPercent)}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-20 bg-gray-500 rounded-full h-2 overflow-hidden">
                        <div
                          className="h-full bg-orange-500"
                          style={{
                            width: `${product.totalRevenue > 0 ? (product.storeRevenue / product.totalRevenue) * 100 : 0}%`
                          }}
                        ></div>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        <span className="text-orange-500">{product.totalRevenue > 0 ? Math.round((product.storeRevenue / product.totalRevenue) * 100) : 0}% store</span>
                        <span className="text-gray-500 ml-1">{product.totalRevenue > 0 ? Math.round((product.onlineRevenue / product.totalRevenue) * 100) : 0}% online</span>
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Note about estimation */}
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
        <div className="flex items-start space-x-2">
          <Calculator className="w-5 h-5 text-yellow-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-yellow-800 dark:text-yellow-200">Estimated Gross Margin</h4>
            <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
              This analysis uses estimated cost ratios for demonstration. In production, actual purchase costs from the GM API would be used for precise margin calculations.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
